// frontend/src/pages/HSCode.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/UIComponents";

export default function HSCode() {
  const nav = useNavigate();

  // ---------- Left: search + list ----------
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [errorList, setErrorList] = useState("");

  // ---------- Right: form ----------
  const empty = {
    hscode_id: null,
    hs_code: "",
    code_desc: "",
    exw_origin: "", // store ORIGIN ID (string)
    imp_tax_rate: 0,
    vat_rate: 0,
    stackable: false,
  };
  const [form, setForm] = useState(empty);
  const [initialForm, setInitialForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [errorSave, setErrorSave] = useState("");
  const [hint, setHint] = useState("");

  // ---------- Origin options ----------
  const [origins, setOrigins] = useState([]);
  const [originsLoading, setOriginsLoading] = useState(false);
  const [originsError, setOriginsError] = useState("");

  const originMapById = useMemo(() => {
    const m = Object.create(null);
    for (const o of origins) m[String(o.opt_id)] = o;
    return m;
  }, [origins]);

  // ---------- Responsive ----------
  const [isNarrow, setIsNarrow] = useState(() =>
    typeof window === "undefined" ? false : window.innerWidth <= 920
  );
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth <= 920);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const qRef = useRef(null);

  // Debounced manual search (no auto-fill anywhere)
  useEffect(() => {
    const t = setTimeout(() => loadList(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  // Initial load (shows up to 200 rows)
  useEffect(() => {
    loadList("");
    loadOrigins();
    setTimeout(() => qRef.current && qRef.current.focus(), 0);
  }, []);

  async function loadList(term) {
    try {
      setLoadingList(true);
      setErrorList("");
      const u = new URL("/api/data", window.location.origin);
      u.searchParams.set("scope", "hscode");
      if (term && term.trim()) u.searchParams.set("q", term.trim());

      const r = await fetch(u.toString(), { method: "GET" });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j?.error || "HTTP " + r.status);
      setRows(Array.isArray(j.rows) ? j.rows : []);
    } catch (e) {
      setErrorList(String(e.message || e));
    } finally {
      setLoadingList(false);
    }
  }

  async function loadOrigins() {
    try {
      setOriginsLoading(true);
      setOriginsError("");
      const u = new URL("/api/data", window.location.origin);
      u.searchParams.set("scope", "origin");
      const r = await fetch(u.toString(), { method: "GET" });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j?.error || "HTTP " + r.status);
      setOrigins(Array.isArray(j.rows) ? j.rows : []);
    } catch (e) {
      setOriginsError(String(e.message || e));
    } finally {
      setOriginsLoading(false);
    }
  }

  // Map stored value (ID or legacy NAME) -> ID for <select>
  const selectedOriginId = useMemo(() => {
    const v = form.exw_origin;
    if (v == null || v === "") return "";
    const s = String(v).trim();
    if (/^\d+$/.test(s)) return s;
    const nameLower = s.toLowerCase();
    const found = origins.find(
      (o) => String(o.opt_name || "").toLowerCase() === nameLower
    );
    return found ? String(found.opt_id) : "";
  }, [form.exw_origin, origins]);

  function originLabelFromValue(v) {
    if (v === null || v === undefined || v === "") return "";
    const s = String(v).trim();
    if (/^\d+$/.test(s)) return originMapById[s]?.opt_name || s;
    return s;
  }

  function selectRow(r) {
    const selected = {
      hscode_id: r.hscode_id,
      hs_code: r.hs_code || "",
      code_desc: r.code_desc || "",
      exw_origin: r.exw_origin || "", // ID string (preferred) or legacy NAME
      imp_tax_rate: num(r.imp_tax_rate),
      vat_rate: num(r.vat_rate),
      stackable: !!r.stackable,
    };
    setForm(selected);
    setInitialForm(selected);
    setHint("Saved changes"); // small status without filling the search box
    // Keep the search box empty → list stays visible & unfiltered
  }

  function newRow() {
    setForm({ ...empty });
    setInitialForm({ ...empty });
    setHint("New entry");
  }

  function resetForm() {
    setForm(initialForm);
    setHint("Reset");
  }

  const changed = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(initialForm),
    [form, initialForm]
  );

  function onChange(e) {
    const { name, type, value, checked } = e.target;
    let v = value;
    if (type === "number") v = value === "" ? "" : Number(value);
    if (type === "checkbox") v = !!checked;
    setForm((prev) => ({ ...prev, [name]: v }));
  }

  function onChangeOrigin(e) {
    setForm((prev) => ({ ...prev, exw_origin: e.target.value })); // store ID (string)
  }

  async function onSave() {
    setErrorSave("");
    setHint("");
    if (!String(form.hs_code || "").trim()) {
      setErrorSave("HS Code is required");
      return;
    }
    const payload = {
      hs_code: str(form.hs_code),
      code_desc: str(form.code_desc),
      exw_origin: str(selectedOriginId || ""), // always send ID (string)
      imp_tax_rate: num(form.imp_tax_rate),
      vat_rate: num(form.vat_rate),
      stackable: !!form.stackable,
    };

    setSaving(true);
    try {
      let r, j;
      if (form.hscode_id) {
        r = await fetch("/api/data?scope=hscode", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ hscode_id: form.hscode_id, ...payload }),
        });
        j = await r.json();
        if (!r.ok || !j.ok) throw new Error(j?.error || "HTTP " + r.status);
        selectRow(j.row);
        setRows((prev) =>
          prev.map((row) => (row.hscode_id === j.row.hscode_id ? j.row : row))
        );
        setHint("Saved changes");
      } else {
        r = await fetch("/api/data?scope=hscode", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        j = await r.json();
        if (!r.ok || !j.ok) throw new Error(j?.error || "HTTP " + r.status);
        setRows((prev) => [j.row, ...prev]);
        selectRow(j.row);
        setHint("Inserted #" + j.row.hscode_id);
      }
    } catch (e) {
      setErrorSave(String(e.message || e));
    } finally {
      setSaving(false);
    }
  }

  function back() {
    nav("/purchase/config");
  }

  // ---------- Simple layout (global classes; minimal inline) ----------
  const grid = {
    display: "grid",
    gridTemplateColumns: isNarrow ? "1fr" : "360px 1fr",
    gap: isNarrow ? 10 : 16,
    alignItems: "start",
  };

  const fieldGrid = {
    display: "grid",
    gridTemplateColumns: isNarrow ? "1fr" : "210px 1fr",
    gap: 10,
    alignItems: "center",
  };

  const smallText = { fontSize: isNarrow ? 11 : 12, opacity: 0.75 };
  const listItemText = isNarrow ? 15 : 14;

  return (
    <div className="app-page">
      <main className="container" style={isNarrow ? { fontSize: 15 } : undefined}>
        <h1 className="menu-title" style={isNarrow ? { fontSize: 22, lineHeight: "24px" } : undefined}>
          Tuiss Web Tools — Purchase
        </h1>
        <h2 style={{ marginTop: -8, ...(isNarrow ? { fontSize: 16 } : {}) }}>
          Thuế nhập khẩu / HS Code
        </h2>

        {/* Top buttons */}
        <div style={{ margin: "8px 0 12px", display: "flex", flexWrap: "wrap", gap: 8 }}>
          <Button onClick={back}>← Trở về / Back</Button>
          <Button onClick={newRow}>New</Button>
          <Button onClick={resetForm} disabled={!changed}>Reset</Button>
          <Button onClick={onSave} disabled={saving || (!changed && !!form.hscode_id)}>
            {saving ? "Saving..." : "Save"}
          </Button>
          {hint ? <span style={{ marginLeft: 4, ...smallText }}>{hint}</span> : null}
        </div>

        <div style={grid}>
          {/* LEFT: manual search + results */}
          <section className="box" style={{ overflow: "hidden" }}>
            <label style={{ display: "block", fontWeight: 600, ...(isNarrow ? { fontSize: 14 } : {}) }}>
              Search / Chọn (HS Code — Mô tả — Xuất xứ)
            </label>
            <input
              ref={qRef}
              className="text-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="type to search..."
            />
            <div style={{ marginTop: 6, ...smallText }}>
              {loadingList ? "Loading..." : rows.length + " item(s)"}
              {errorList ? <span style={{ color: "crimson", marginLeft: 8 }}>{errorList}</span> : null}
            </div>

            <div
              style={{
                marginTop: 8,
                maxHeight: isNarrow ? 320 : 440,
                overflow: "auto",
                borderTop: "1px solid #eee",
              }}
            >
              {rows.map((r) => {
                const sel = r.hscode_id === form.hscode_id && form.hscode_id != null;
                const originName = originLabelFromValue(r.exw_origin) || "(no origin)";
                return (
                  <button
                    key={r.hscode_id}
                    onClick={() => selectRow(r)}
                    className="menu-card"
                    style={{
                      width: "100%",
                      display: "block",
                      textAlign: "left",
                      padding: isNarrow ? "10px 10px" : "8px 10px",
                      border: "none",
                      borderBottom: "1px solid #f0f0f0",
                      borderRadius: 0,
                      background: sel ? "rgba(0,0,0,0.05)" : "transparent",
                      fontSize: listItemText,
                    }}
                    title="Select"
                  >
                    <div style={{ fontWeight: 600 }}>
                      {(r.hs_code || "(no HS code)")} — {(r.code_desc || "(no desc)")} — {originName}
                    </div>
                    <div style={{ marginTop: 2, ...smallText }}>
                      {"id #" + r.hscode_id} {" • Import Tax " + fmtNum(r.imp_tax_rate)} {" • VAT " + fmtNum(r.vat_rate)} {" • Stackable " + (r.stackable ? "Yes" : "No")}
                    </div>
                  </button>
                );
              })}
              {rows.length === 0 && !loadingList ? (
                <div style={{ padding: 8, ...smallText }}>No results</div>
              ) : null}
            </div>
          </section>

          {/* RIGHT: editable form */}
          <section className="box" style={{ marginTop: isNarrow ? 12 : 0 }}>
            <div style={fieldGrid}>
              <label>HS Code</label>
              <input
                className="text-input"
                name="hs_code"
                value={form.hs_code}
                onChange={onChange}
                placeholder="e.g., 7308.90.00"
              />

              <label>Description</label>
              <textarea
                className="text-input"
                name="code_desc"
                value={form.code_desc}
                onChange={onChange}
                rows={3}
                placeholder="Short description"
              />

              <label>Goods Origin</label>
              <select
                className="text-input"
                name="exw_origin_select"
                value={selectedOriginId}
                onChange={onChangeOrigin}
                disabled={originsLoading || !!originsError}
                title={originsError ? originsError : "Select origin"}
              >
                <option value="">{originsLoading ? "Loading..." : "-- Select origin --"}</option>
                {origins.map((o) => (
                  <option key={o.opt_id} value={String(o.opt_id)} title={o.opt_desc || ""}>
                    {o.opt_name}
                  </option>
                ))}
              </select>

              <label>Import Tax (%)</label>
              <input
                className="text-input"
                type="number"
                step="0.01"
                min="0"
                name="imp_tax_rate"
                value={form.imp_tax_rate}
                onChange={onChange}
                placeholder="0"
              />

              <label>VAT (%)</label>
              <input
                className="text-input"
                type="number"
                step="0.01"
                min="0"
                name="vat_rate"
                value={form.vat_rate}
                onChange={onChange}
                placeholder="0"
              />

              <label>Stackable</label>
              <input
                type="checkbox"
                name="stackable"
                checked={!!form.stackable}
                onChange={onChange}
                style={{ width: isNarrow ? 22 : 20, height: isNarrow ? 22 : 20 }}
              />
            </div>

            {errorSave ? (
              <div style={{ color: "crimson", marginTop: 10 }}>{errorSave}</div>
            ) : (
              <div style={{ marginTop: 10, ...smallText }}>
                {originsLoading ? (
                  "Loading origin options…"
                ) : originsError ? (
                  <span style={{ color: "crimson" }}>{originsError}</span>
                ) : (
                  "Origins loaded: " + origins.length
                )}
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}

// ---------- helpers ----------
function str(v) {
  if (v === null || v === undefined) return "";
  return String(v).trim();
}
function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}
function fmtNum(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}